"use client";

import {
	type ReactNode,
	createContext,
	useCallback,
	useContext,
	useState,
} from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

/** What the current step publishes to the payload panel. */
export type StepPayloads = {
	flowContext?: unknown;
	submitPayload?: unknown;
};

type DevPreviewContextValue = {
	previewStateId: string | null;
	setPreviewState: (stateId: string | null) => void;
	showDevTools: boolean;
	payloadPreview: StepPayloads | undefined;
	setPayloadPreview: (payloads: StepPayloads | undefined) => void;
};

const DevPreviewContext = createContext<DevPreviewContextValue | null>(null);

const DevPreviewProviderInner = ({
	children,
	showDevTools,
}: {
	children: ReactNode;
	showDevTools: boolean;
}) => {
	const router = useRouter();
	const pathname = usePathname();
	const searchParams = useSearchParams();
	const previewStateId = searchParams.get("devState");
	const [payloadPreview, setPayloadPreview] = useState<
		StepPayloads | undefined
	>(undefined);

	const setPreviewState = useCallback(
		(stateId: string | null) => {
			const params = new URLSearchParams(searchParams.toString());
			if (stateId) {
				params.set("devState", stateId);
			} else {
				params.delete("devState");
			}

			const query = params.toString();
			router.replace(query ? `${pathname}?${query}` : pathname, {
				scroll: false,
			});
		},
		[pathname, router, searchParams]
	);

	return (
		<DevPreviewContext.Provider
			value={{
				previewStateId,
				setPreviewState,
				showDevTools,
				payloadPreview,
				setPayloadPreview,
			}}
		>
			{children}
		</DevPreviewContext.Provider>
	);
};

export const DevPreviewProvider = ({
	children,
	showDevTools,
}: {
	children: ReactNode;
	showDevTools: boolean;
}) => {
	// Dev tools read `useSearchParams()`, which cannot run unguarded in a
	// statically prerendered tree (the root `loading.tsx` fallback renders
	// during the `/_not-found` prerender). Mock flows are always dynamic.
	if (!showDevTools) {
		return <>{children}</>;
	}

	return (
		<DevPreviewProviderInner showDevTools>{children}</DevPreviewProviderInner>
	);
};

export const useDevPreview = (): DevPreviewContextValue => {
	const context = useContext(DevPreviewContext);
	if (!context) {
		throw new Error("useDevPreview must be used within DevPreviewProvider");
	}
	return context;
};

export const useIsDevToolsEnabled = (): boolean =>
	useContext(DevPreviewContext)?.showDevTools ?? false;

const noop = () => {};

/**
 * Publishes a payload into the dev panel's payload preview. Null-safe: returns a
 * no-op when rendered outside `DevPreviewProvider`.
 */
export const useSetDevPayloadPreview = (): ((
	payloads: StepPayloads | undefined
) => void) => useContext(DevPreviewContext)?.setPayloadPreview ?? noop;
